import { useQuery } from '@apollo/client';
import { graphql } from '@client/graphql/types';
import { useMemo } from 'react';
import { DateRange, TrendsAnalyticsService } from '../../common/reservation-analytic-service';

const QUERY = graphql(`
  query analytic_customerSummary($branchId: ID!, $startTime: DateTime!, $endTime: DateTime!) {
    analytic_customerSummary(branchId: $branchId, startTime: $startTime, endTime: $endTime) {
      newCustomers
      returningCustomers
    }
  }
`);

export default function useGetCustomerSummaryMetrics(branchId: string, dateRange: DateRange) {
  const previousDateRange = useMemo(() => TrendsAnalyticsService.getPreviousDateRange(dateRange), [dateRange]);

  // Current period query
  const {
    data: currentData,
    refetch: currentRefetch,
    loading: currentLoading,
    error: currentError,
  } = useQuery(QUERY, {
    variables: { branchId, startTime: dateRange.startDate, endTime: dateRange.endDate },
  });

  // Previous period query
  const {
    data: previousData,
    refetch: previousRefetch,
    loading: previousLoading,
    error: previousError,
  } = useQuery(QUERY, {
    variables: {
      branchId,
      startTime: previousDateRange.startDate,
      endTime: previousDateRange.endDate,
    },
  });

  // Computed values
  const analytics = useMemo(() => {
    if (!currentData?.analytic_customerSummary || !previousData?.analytic_customerSummary) {
      return null;
    }

    const current = currentData.analytic_customerSummary;
    const previous = previousData.analytic_customerSummary;
    const trends = {
      newCustomers: TrendsAnalyticsService.calculateTrend(current.newCustomers, previous.newCustomers),
      returningCustomers: TrendsAnalyticsService.calculateTrend(
        current.returningCustomers,
        previous.returningCustomers,
      ),
    };

    return {
      current,
      previous,
      trends,
    };
  }, [currentData, previousData]);

  const isLoading = currentLoading || previousLoading;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const error = currentError || previousError;

  return {
    analytics,
    isLoading,
    error,
    refetch: () => {
      // Refetch both queries
      return Promise.all([currentRefetch(), previousRefetch()]);
    },
  };
}
